import React, { useEffect, useRef, useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Bold, Italic, List, ListOrdered, Link2, Unlink, Undo2, Redo2 } from 'lucide-react';

/**
 * The email body field, rich text or raw HTML (task: WYSIWYG email editor,
 * 2026-09-24).
 *
 * CRITICAL FINDING, tested before any of this was built: Tiptap + StarterKit
 * alone (no Table or Image extension, per the owner's own call, ship
 * StarterKit only for now) cannot round trip the real DEFAULT_EMAIL_BODY_TEMPLATE
 * or ADAM_SIGNATURE. A direct test (generateJSON/generateHTML against both
 * real strings) showed: the outer styled <div> wrapper gone entirely, the
 * {personPhoto} <img> line deleted outright, the calendar link's brand-orange
 * inline style stripped, and for the signature, its entire nested <table>
 * layout AND all 6 images gone, collapsing a hand tuned two column signature
 * card into two bare paragraphs of plain text. Silently: no error, no
 * warning, just missing content the moment it round trips through this
 * schema and gets saved.
 *
 * hasComplexHtml below is the guard that came out of that finding: any body
 * containing a <table>, an <img>, a <div> carrying its own style attribute,
 * or an <a> tag missing a real href (this covers every real template and
 * signature in this system today) opens in raw HTML by default, never the
 * rich editor, no exceptions. A body with none of that (genuinely new,
 * simple content) defaults to rich text instead. The toggle below is always
 * available either way: someone can still drop into raw HTML on simple
 * content, or force rich text on complex content if they explicitly choose
 * to, with a standing warning while they do.
 *
 * The <a> check, added 2026-09-24: a real, contact-facing calendar link on
 * FS/TEC went out with no href at all (worth reading as a warning sign in
 * its own right, not proof of a cause: tested against a real Tiptap round
 * trip and confirmed it actually PRESERVES a placeholder-style href like
 * {calendarLink} rather than stripping it, so that specific incident traces
 * to how the template was authored, not to this editor; see the incident
 * notes for 2026-09-24). Still worth guarding here regardless of how a link
 * without a real href gets created: Tiptap has no extension for arbitrary
 * inline styles (no TextStyle/Color), so a real round trip on ANY templated
 * link, whether or not its href itself survives, silently drops that link's
 * brand-orange color/weight, adds target/rel it did not have, and (per the
 * same test) turns an inline font-weight:600 into an actual <strong> tag.
 * A link whose href is missing, blank, or still a raw {placeholder} (never
 * substituted before this check runs, since the stored template is what
 * this checks, not a rendered email) is flagged the same way: either the
 * href needs a rep to notice it is wrong, or it is a real placeholder that
 * will only resolve later, at send time, neither of which the rich editor
 * can represent safely today.
 */
export function hasComplexHtml(html) {
  if (!html) return false;
  if (/<table[\s>]/i.test(html)) return true;
  if (/<img[\s>]/i.test(html)) return true;
  if (/<div\b[^>]*\sstyle\s*=/i.test(html)) return true;

  const anchors = html.match(/<a\b[^>]*>/gi) || [];
  for (const tag of anchors) {
    const hrefMatch = tag.match(/\bhref\s*=\s*(?:"([^"]*)"|'([^']*)')/i);
    const href = hrefMatch ? (hrefMatch[1] ?? hrefMatch[2] ?? '') : null;
    if (href === null || !href.trim() || href.includes('{')) return true;
  }

  return false;
}

function ToolbarButton({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      // Keeps focus (and the current text selection) inside the editor
      // itself, not on the toolbar button just tapped, which is what a
      // plain button click would otherwise steal right before the
      // formatting command needs that same selection to act on.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`h-7 w-7 flex items-center justify-center rounded-md border disabled:opacity-40 disabled:cursor-not-allowed ${
        active ? 'bg-accent/20 border-accent text-accent' : 'bg-secondary/50 border-transparent text-foreground hover:bg-secondary'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * The live Tiptap instance. Split out from EmailBodyEditor below on purpose:
 * useEditor is a real React hook, and has to be called unconditionally on
 * every render of whatever component calls it, which a dynamic import
 * cannot do inline (the module, and the hook function it exports, do not
 * exist yet on the first render). Conditionally MOUNTING this component
 * only once the dynamically imported modules are ready, rather than
 * conditionally calling the hook inside one always-mounted component, is
 * what makes a dynamically loaded editor library and the Rules of Hooks
 * compatible.
 */
function TiptapEditor({ tiptap, value, onChange, disabled, ghostClassName, isGhost }) {
  const { useEditor, EditorContent } = tiptap;
  // What this editor itself last emitted via onUpdate, so the sync effect
  // below can tell "the value prop changed because I typed" apart from "the
  // value prop changed because a different rep/show loaded, or Reset to
  // industry default ran" without ever comparing to a stale closure.
  const lastEmitted = useRef(value);

  const editor = useEditor({
    extensions: [tiptap.StarterKit],
    content: value || '',
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[160px] px-3 py-2',
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastEmitted.current = html;
      onChange(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    // false: do not re-emit onUpdate for a change that came from outside,
    // there is nothing new here for onChange to record.
    editor.commands.setContent(value || '', false);
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL', previous);
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  return (
    <div className={`rounded-md border border-input bg-transparent ${isGhost ? ghostClassName : ''}`}>
      <div className="flex flex-wrap items-center gap-1 border-b border-border p-1">
        <ToolbarButton title="Bold" active={editor.isActive('bold')} disabled={disabled} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Italic" active={editor.isActive('italic')} disabled={disabled} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Bullet list" active={editor.isActive('bulletList')} disabled={disabled} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Numbered list" active={editor.isActive('orderedList')} disabled={disabled} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton title={editor.isActive('link') ? 'Edit link' : 'Add link'} active={editor.isActive('link')} disabled={disabled} onClick={setLink}>
          <Link2 className="w-3.5 h-3.5" />
        </ToolbarButton>
        {editor.isActive('link') && (
          <ToolbarButton title="Remove link" disabled={disabled} onClick={() => editor.chain().focus().unsetLink().run()}>
            <Unlink className="w-3.5 h-3.5" />
          </ToolbarButton>
        )}
        <span className="w-px h-5 bg-border mx-0.5" />
        <ToolbarButton title="Undo" disabled={disabled || !editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Redo" disabled={disabled || !editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 className="w-3.5 h-3.5" />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

/** Dynamically imported, same pattern as heic2any/DOMPurify elsewhere in this app (RepProfileFields.jsx): only loaded for someone actually looking at a body field in rich text mode, never on every visit to this page. */
function useTiptapModules(enabled) {
  const [tiptap, setTiptap] = useState(null);

  useEffect(() => {
    if (!enabled || tiptap) return;
    let cancelled = false;
    Promise.all([import('@tiptap/react'), import('@tiptap/starter-kit')]).then(([react, starterKit]) => {
      if (cancelled) return;
      setTiptap({ useEditor: react.useEditor, EditorContent: react.EditorContent, StarterKit: starterKit.default });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, tiptap]);

  return tiptap;
}

/**
 * value/onChange are the same plain HTML string TemplateFields' body field
 * always used: render(), unknownPlaceholders(), and both save paths
 * (worker/routes/shows.ts's PATCH, the rep_config settings write) are
 * completely unchanged by this, since neither view produces anything other
 * than that same string. Placeholders ({firstName}, {repName}, ...) are
 * just text content to both views, so they type, display, and round trip
 * through either one exactly as they always did.
 */
export default function EmailBodyEditor({ id, value, onChange, disabled, placeholder, isGhost, ghostClassName }) {
  const [override, setOverride] = useState(null); // 'rich' | 'raw' | null (follow auto-detection)
  const lastSeenValue = useRef(value);

  // Fresh judgment for genuinely different content: a manual override made
  // for one rep's or show's body must not silently carry over and hide the
  // warning for a different one that later loads into this same mounted
  // field (switching the Per-Rep Override dropdown does not remount
  // TemplateFields, it just changes this same prop).
  useEffect(() => {
    if (value === lastSeenValue.current) return;
    lastSeenValue.current = value;
    setOverride(null);
  }, [value]);

  const complex = hasComplexHtml(value);
  const mode = override ?? (complex ? 'raw' : 'rich');
  const tiptap = useTiptapModules(mode === 'rich');

  return (
    <div className="mt-1">
      <div className="flex items-center justify-end gap-1 mb-1">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOverride('rich')}
          className={`text-[10px] px-2 py-0.5 rounded-md border disabled:opacity-40 ${
            mode === 'rich' ? 'bg-accent/20 border-accent text-accent font-medium' : 'border-transparent text-muted-foreground hover:bg-secondary/50'
          }`}
        >
          Rich text
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOverride('raw')}
          className={`text-[10px] px-2 py-0.5 rounded-md border disabled:opacity-40 ${
            mode === 'raw' ? 'bg-accent/20 border-accent text-accent font-medium' : 'border-transparent text-muted-foreground hover:bg-secondary/50'
          }`}
        >
          Raw HTML
        </button>
      </div>

      {mode === 'rich' && complex && (
        <p className="flex items-start gap-1.5 text-[11px] text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2 mb-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            This content has a table, image or styled div the rich text editor cannot represent. Saving from
            here will remove that formatting. Switch to Raw HTML to keep it exactly as it is.
          </span>
        </p>
      )}

      {mode === 'raw' ? (
        <Textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={20}
          placeholder={placeholder}
          className={`md:text-xs font-mono resize-none ${isGhost ? ghostClassName : ''}`}
        />
      ) : tiptap ? (
        <TiptapEditor tiptap={tiptap} value={value} onChange={onChange} disabled={disabled} isGhost={isGhost} ghostClassName={ghostClassName} />
      ) : (
        <div className="flex items-center justify-center h-40 rounded-md border border-input text-xs text-muted-foreground">
          Loading editor...
        </div>
      )}
    </div>
  );
}
